import {
  Activity,
  Apple,
  Award,
  BookOpen,
  Briefcase,
  Calculator,
  ClipboardList,
  Code,
  Cpu,
  FlaskConical,
  GraduationCap,
  HandHeart,
  HeartPulse,
  Landmark,
  Laptop,
  Pill,
  Scale,
  Stethoscope,
  Truck,
  TrendingUp,
  Users,
} from "lucide-react";

const ICON_STROKE = 1.5;

const schoolIcon = (name) => {
  const n = name.toLowerCase();
  if (n.includes("health")) return HeartPulse;
  if (n.includes("business") || n.includes("social")) return Award;
  if (n.includes("computer")) return Cpu;
  if (n.includes("law")) return Scale;
  return BookOpen;
};

function degreeIcon(title) {
  const t = title.toLowerCase();
  if (t.includes("medicine") || t.includes("surgery")) return Stethoscope;
  if (t.includes("laboratory")) return FlaskConical;
  if (t.includes("nursing")) return Activity;
  if (t.includes("midwifery")) return HeartPulse;
  if (t.includes("pharmacy")) return Pill;
  if (t.includes("nutrition") || t.includes("food")) return Apple;
  if (t.includes("human resource")) return Users;
  if (t.includes("banking") || t.includes("finance") || t.includes("islamic banking"))
    return Landmark;
  if (t.includes("accounting")) return Calculator;
  if (t.includes("economics")) return TrendingUp;
  if (t.includes("social work")) return HandHeart;
  if (t.includes("business") || t.includes("mba")) return Briefcase;
  if (t.includes("logistics") || t.includes("procurement") || t.includes("supply chain"))
    return Truck;
  if (t.includes("software")) return Code;
  if (t.includes("information") || t.includes("communication")) return Laptop;
  if (t.includes("law") || t.includes("llb")) return Scale;
  if (t.includes("public health")) return Activity;
  if (t.includes("health service")) return HeartPulse;
  if (t.includes("project planning")) return ClipboardList;
  if (t.includes("education")) return GraduationCap;
  return BookOpen;
}

function ProgramCard({ title }) {
  const Icon = degreeIcon(title);
  return (
    <article className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-7 shadow-[0_2px_16px_rgba(15,23,42,0.06)] transition hover:border-slate-300 hover:shadow-[0_4px_24px_rgba(15,23,42,0.08)] sm:p-8">
      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-maroon-900 text-white shadow-md shadow-maroon-900/30">
        <Icon className="h-6 w-6" strokeWidth={ICON_STROKE} aria-hidden />
      </span>
      <p className="mt-5 flex-1 text-lg font-bold tracking-tight leading-snug text-slate-900">
        {title}
      </p>
    </article>
  );
}

function FacultyCard({ school }) {
  const HeaderIcon = schoolIcon(school.name);
  const programsSummary = school.degrees.join(" · ");

  return (
    <article className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-7 shadow-[0_2px_16px_rgba(15,23,42,0.06)] transition hover:border-slate-300 hover:shadow-[0_4px_24px_rgba(15,23,42,0.08)] sm:p-8">
      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-maroon-900 text-white shadow-md shadow-maroon-900/30">
        <HeaderIcon className="h-6 w-6" strokeWidth={ICON_STROKE} aria-hidden />
      </span>
      <h4 className="mt-5 text-lg font-bold tracking-tight text-slate-900">{school.name}</h4>
      <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-600">{programsSummary}</p>
    </article>
  );
}

export function ProgramsPage({ uni, logoSrc }) {
  return (
    <main id="main" className="flex-1">
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-10">
        <ProgramsSection uni={uni} logoSrc={logoSrc} />
      </div>
    </main>
  );
}

export function ProgramsSection({ uni, logoSrc }) {
  const prog = uni.programsOffered;
  return (
    <section id="programs" className="scroll-mt-28 sm:mt-0" aria-labelledby="programs-heading">
      <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-maroon-700">
            Academics
          </p>
          <h1
            id="programs-heading"
            className="mt-2 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl"
          >
            Programs offered
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-600 sm:text-base">
            Master&apos;s degrees and graduate pathways, alongside undergraduate schools of
            health, business, computing, and law.
          </p>
        </div>
        <img
          src={logoSrc}
          alt=""
          className="hidden h-14 w-14 rounded-xl border border-slate-200 bg-white object-contain p-1 shadow-sm sm:block"
          width={56}
          height={56}
        />
      </div>

      {prog ? (
        <div className="flex flex-col gap-12">
          {prog.columns[1]?.masters ? (
            <section
              id="master-programs"
              className="scroll-mt-28 space-y-6"
              aria-labelledby="masters-heading"
            >
              <h2
                id="masters-heading"
                className="flex items-center gap-2 border-l-4 border-maroon-700 pl-4 text-lg font-bold text-slate-900"
              >
                <GraduationCap
                  className="h-5 w-5 text-maroon-700 opacity-90"
                  strokeWidth={ICON_STROKE}
                  aria-hidden
                />
                {prog.columns[1].masters.title}
              </h2>
              <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {prog.columns[1].masters.degrees.map((d) => (
                  <ProgramCard key={d} title={d} />
                ))}
              </div>
            </section>
          ) : null}

          <div className="space-y-6">
            <h2 className="flex items-center gap-2 border-l-4 border-maroon-700 pl-4 text-lg font-bold text-slate-900">
              <BookOpen
                className="h-5 w-5 text-maroon-700 opacity-90"
                strokeWidth={ICON_STROKE}
                aria-hidden
              />
              {prog.leftColumnTitle}
            </h2>
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {prog.columns[0]?.schools.map((school) => (
                <FacultyCard key={school.name} school={school} />
              ))}
            </div>
          </div>

          <div className="space-y-6">
            <h2 className="flex items-center gap-2 border-l-4 border-maroon-700 pl-4 text-lg font-bold text-slate-900">
              <Cpu
                className="h-5 w-5 text-maroon-700 opacity-90"
                strokeWidth={ICON_STROKE}
                aria-hidden
              />
              {prog.rightColumnTitle}
            </h2>
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {prog.columns[1]?.schools.map((school) => (
                <FacultyCard key={school.name} school={school} />
              ))}
            </div>
          </div>

          <section
            id="degree-programs-complete"
            className="scroll-mt-28 rounded-2xl border border-slate-200 bg-white p-6 shadow-soft sm:p-8"
            aria-labelledby="degree-list-heading"
          >
            <h2 id="degree-list-heading" className="text-lg font-bold text-slate-900 sm:text-xl">
              Complete degree listing
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">
              All undergraduate and master programs as listed on the{" "}
              {uni.featuredCampus?.name ?? "Berbera campus"} brochure.
            </p>

            <div className="mt-8 grid gap-10 lg:grid-cols-2 lg:gap-12">
              <div>
                <p className="border-b border-maroon-100 pb-2 text-sm font-bold uppercase tracking-wide text-maroon-900">
                  Degree programs
                </p>
                <div className="mt-6 space-y-8">
                  {prog.columns[0]?.schools.map((school) => (
                    <div key={school.name}>
                      <p className="text-sm font-semibold text-slate-900">{school.name}</p>
                      <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm leading-relaxed text-slate-600 marker:font-medium marker:text-maroon-800">
                        {school.degrees.map((d) => (
                          <li key={d} className="pl-1">
                            {d}
                          </li>
                        ))}
                      </ol>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <p className="border-b border-maroon-100 pb-2 text-sm font-bold uppercase tracking-wide text-maroon-900">
                  Degree &amp; master programs
                </p>
                <div className="mt-6 space-y-8">
                  {prog.columns[1]?.schools.map((school) => (
                    <div key={school.name}>
                      <p className="text-sm font-semibold text-slate-900">{school.name}</p>
                      <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm leading-relaxed text-slate-600 marker:font-medium marker:text-maroon-800">
                        {school.degrees.map((d) => (
                          <li key={d} className="pl-1">
                            {d}
                          </li>
                        ))}
                      </ol>
                    </div>
                  ))}
                  {prog.columns[1]?.masters ? (
                    <div>
                      <p className="text-sm font-semibold text-slate-900">
                        {prog.columns[1].masters.title}
                      </p>
                      <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm leading-relaxed text-slate-600 marker:font-medium marker:text-maroon-800">
                        {prog.columns[1].masters.degrees.map((d) => (
                          <li key={d} className="pl-1">
                            {d}
                          </li>
                        ))}
                      </ol>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </section>
        </div>
      ) : null}
    </section>
  );
}
